import { TOKEN_RECOVERY_EPISODE_TIMEOUT_MS } from '../tokenRecovery';
import {
  attachTokenRecoveryDiagnostics,
  createTokenRequestDiagnosticSummary,
  getTokenRequestDiagnostics
} from '../tokenResponseDiagnostics';
import type { TokenRecoveryDiagnostic } from '../tokenRecoveryDiagnostics';
import type { TokenData, TokenRefreshCallback, TokenRefreshAttemptResult } from './types';

const TERMINAL_REFRESH_FAILURE_STATUSES = new Set([401, 403]);

export const createTokenRefreshFailureResult = (error: unknown): TokenRefreshAttemptResult => {
  const requestError = error as {
    response?: { status?: number };
    code?: string;
    name?: string;
  } | null;
  const diagnostics = getTokenRequestDiagnostics(error);
  const status = diagnostics?.status ?? requestError?.response?.status;
  const diagnosticSummary = createTokenRequestDiagnosticSummary(diagnostics);

  return {
    success: false,
    error: 'Token refresh request failed',
    status,
    ...(diagnosticSummary && { diagnosticSummary }),
    terminalFailure:
      (status !== undefined && TERMINAL_REFRESH_FAILURE_STATUSES.has(status)) ||
      requestError?.code === 'CHAT_REFRESH_STATE_UNAVAILABLE' ||
      requestError?.name === 'TerminalChatAuthError'
  };
};

interface RefreshOwner {
  tabId: string;
  isLeader: () => boolean;
  isCurrentAuthUser: (userId: string) => boolean;
}

export class RefreshExecution {
  private callback: TokenRefreshCallback | null = null;

  private activePromise: Promise<TokenData | null> | null = null;

  private activeRecovery: TokenRecoveryDiagnostic | undefined;

  private completed: {
    completedAt: number;
    promise: Promise<TokenData | null>;
    requestedByUserId: string;
  } | null = null;

  private readonly owner: RefreshOwner;

  private isDestroyed = false;

  constructor(owner: RefreshOwner) {
    this.owner = owner;
  }

  public setCallback(callback: TokenRefreshCallback): void {
    this.callback = callback;
  }

  public isActive(): boolean {
    return this.activePromise !== null;
  }

  public getActiveRecovery(): TokenRecoveryDiagnostic | undefined {
    return this.activeRecovery;
  }

  public clearCompleted(): void {
    this.completed = null;
  }

  public destroy(): void {
    this.isDestroyed = true;
    this.clearCompleted();
  }

  public async execute(
    requestedByUserId?: string | null,
    requestStartedAt?: number,
    recovery?: TokenRecoveryDiagnostic
  ): Promise<TokenData | null> {
    if (!this.callback) {
      return null;
    }

    if (this.activePromise) {
      return this.activePromise;
    }

    const reusableRefresh = this.getReusableCompleted(requestedByUserId, requestStartedAt);

    if (reusableRefresh) {
      return reusableRefresh;
    }

    const ownerRecovery = recovery && {
      ...recovery,
      tabId: this.owner.tabId,
      ownerTabId: this.owner.tabId,
      role: this.owner.isLeader() ? ('leader' as const) : ('delegated' as const),
      ownerResponded: true,
      ownerRequestSent: false,
      online: navigator.onLine,
      visibility: document.visibilityState === 'hidden' ? ('hidden' as const) : ('visible' as const)
    };
    this.activeRecovery = ownerRecovery;
    const refreshPromise = this.callback(requestedByUserId, ownerRecovery)
      .then(
        tokenData =>
          tokenData && { ...tokenData, ...(ownerRecovery && { recovery: ownerRecovery }) }
      )
      .catch(error => {
        if (ownerRecovery) {
          attachTokenRecoveryDiagnostics(error, ownerRecovery);
        }
        throw error;
      });
    this.activePromise = refreshPromise;

    const completeRefresh = () => {
      if (this.activePromise === refreshPromise) {
        this.activePromise = null;
        this.activeRecovery = undefined;
      }

      if (
        !this.isDestroyed &&
        requestedByUserId &&
        this.owner.isCurrentAuthUser(requestedByUserId)
      ) {
        this.completed = {
          completedAt: Date.now(),
          promise: refreshPromise,
          requestedByUserId
        };
      }
    };

    refreshPromise.then(completeRefresh, completeRefresh);

    return refreshPromise;
  }

  private getReusableCompleted(
    requestedByUserId?: string | null,
    requestStartedAt?: number
  ): Promise<TokenData | null> | null {
    const completedRefresh = this.completed;

    if (
      !completedRefresh ||
      !requestedByUserId ||
      completedRefresh.requestedByUserId !== requestedByUserId ||
      typeof requestStartedAt !== 'number' ||
      !Number.isFinite(requestStartedAt)
    ) {
      return null;
    }

    const completionDelay = completedRefresh.completedAt - requestStartedAt;
    const completedAge = Date.now() - completedRefresh.completedAt;

    if (
      completionDelay < 0 ||
      completionDelay > TOKEN_RECOVERY_EPISODE_TIMEOUT_MS ||
      completedAge < 0 ||
      completedAge > TOKEN_RECOVERY_EPISODE_TIMEOUT_MS
    ) {
      return null;
    }

    return completedRefresh.promise;
  }
}
